/** @module csi/slicer/slicerEditor */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/store/Memory",
	"dijit/CheckedMenuItem",
	"dojo/mouse",
	"dojo/query",
	"dojo/topic",
	"dijit/Destroyable",
	"dijit/form/Button",
	"dijit/form/DropDownButton",
	"dijit/form/ToggleButton",
	"dijit/layout/ContentPane",
	"dijit/popup",
	"dijit/TitlePane",
	"dgrid/Selection",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/action",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/constants",
	"csi/def",
	"csi/def/Action",
	"csi/dgrid/Manager",
	"csi/dojox/layout/GridContainerLite",
	"csi/widget/CheckBox",
	"csi/widget/DropDownMenu",
	"csi/widget/MessageDialog",
	"csi/html",
	"csi/locale",
	"csi/store/Names",
	"csi/types",
	"csi/xml",
	"csi/xml/Search",
	"csi/view/ViewDialog",
	"csi/widget/InlineActionMenu",
	"csi/widget/InvalidMessage",
	"csi/widget/SelectList",
	"./slicerColorDlg",
	"./slicerCompareDlg",
	"./slicerMetricsDlg"
], function (declare, lang, domClass, Memory, CheckedMenuItem, mouse, query, topic, Destroyable, Button, DropDownButton, ToggleButton, ContentPane, popup,
	TitlePane, Selection, _, crews, csiAction, Controller, PostRequest, constants, def, Action, Manager, GridContainerLite, CheckBox, DropDownMenu, MessageDialog, html, locale,
	Names, types, xml, Search, ViewDialog, InlineActionMenu, InvalidMessage, SelectList, SlicerColorDlg, slicerCompareDlg, slicerMetricsDlg) {

	/** Drag and drop types indexes for this.dndTypes */
	var DND_TYPE_CLASSIFIER = 0;
	var DND_TYPE_METRICS = 1;

	var CHAR_LT = '&#x003C'; // less then unicode
	var CHAR_LTE = '<span class="csi-slicer-special-char">&#x2264;</span>'; // less then or equal unicode
	var CHAR_GT = '&#x003E'; // greater then unicode
	var CHAR_GTE = '<span class="csi-slicer-special-char">&#x2265;</span>'; // greated then or equal unicode

	/** Constant for Classifier pane - the same pane used for row, column classifiers and metrics. */
	var B_UNKNOWN = 0;
	var B_ROW_CLS = 1;
	var B_COL_CLS = 2;
	var B_METRICS = 3;

	var DELIMITER_CHAR = '\x001';
	var MARK_COMPARE = DELIMITER_CHAR + 'compare';

	/**
	 * Used to reactivate metric pane after view rebuild;
	 * @property {string} activePaneCls - panel with this cls will be expanded
	 */
	var activePaneCls = '';
	/** @property {Number} scrollPos - vertical scroll position to scroll to after view rebuild */
	var scrollPos = 0;

	/**
	 * Controller to be used with Custom Metric deletion
	 * Purpose - allow to have variable confirmation message, depend on metric usage in various CVs
	 * Function checkMetricInUse creates extended Confirmation message if needed
	 */
	var DeleteMetricController = declare([Controller], {
		confirmAction: function (/*Object*/params) {
			params.message = this.getAction().confirmMessage;
			this.inherited(arguments);
		}
	});

	/**
	 * Action to delete Custom Metric
	 * Used in "Manage Metrics" dialog and in each Custom Metric's popup menu
	 */
	var DeleteMetricAction = declare([Action], {
		classController: DeleteMetricController,
		m_id: "SlicerDeleteCustomMetric",
		m_typeControl: def.LINK,
		m_preAction: checkMetricInUse,
		confirmAction: MessageDialog.BUTTON_DELETE,
		m_name: locale.resource("SlicerDeleteCustomMetric")
	});

	/**
	 * Function creates extended Confirmation message (if needed) for Custom Metric delete
	 * @param {Controller} ac - action controller
	 * @return {Deferred}
	 */
	function checkMetricInUse(ac) {
		var action = ac.getAction();
		var metricId = action.metric.id;
		var viewKey = action.viewKey;
		var customViewId = action.customViewId;
		var s = new Search();
		s.byType("_CS_PreferenceView");
		s.getQuery().Attr("Options", xml.OP_MATCHES, '%"metricKeys"%"' + metricId + '"%');
		s.getQuery().RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, "centric://PREFERENCE/" + viewKey);
		s.getQuery().Node(xml.PROP_URL, xml.OP_NOT_EQUALS, customViewId);
		var cvStore = new Names({
			request: s.getRequest()
		});
		return cvStore.query().then(function (results) {
			var usedIn = _.pluck(results, cvStore.nameProperty);
			action.confirmMessage = usedIn.length ? locale.resource("SlicerDeleteCustomMetric_ConfirmLong", [usedIn.join(", ")]) : "";
			return true;
		});
	}

	return declare([Destroyable], {
		/** @property {ContentPane} _SlicerEditorContainer */
		_SlicerEditorContainer: null,
		/** @property {Node} _domNode - Container Node */
		_domNode: null,
		/** @property {boolean} _created - flag SlicerEditor is already created */
		_created: false,
		/** @property {Object} _config - SlicerConfiguration */
		_config: {},
		/** @property {Object} _cfgHandle - Configuration subscription handle */
		_cfgHandle: null,
		/** @property {string[]} dndType - dnd source type acepted for classifiers*/
		dndTypes: [_.uniqueId(), _.uniqueId()],
		/** @property {string[]} dndType - dnd source type acepted for metrics*/
		dndTypesMetrics: [_.uniqueId()],
		/** {boolean} _cbExpandAll - checkbox "expand all" */
		_cbExpandAll: null,
		/** @property {SlicerConfig} _slicerConfig */
		_slicerConfig: null,
		/** @property {Object} widget */
		_slicerWidget: null,
		/** @property {boolean} refreshRequired */
		refreshRequired: false,

		/**
		 * Return Spec for given key
		 * @param {String} id
		 */
		_getSpec: function (id) {
			return this._slicerWidget.getSpec(id);
		},

		/**
		 * Function _init should be called only once.
		 * @param {ContentPane} SlicerEditorContainer
		 */
		init: function (SlicerEditorContainer, slicerConfig, widget) {
			this._created = false;
			this._slicerConfig = slicerConfig;
			this._slicerWidget = widget;
			this._domNode = SlicerEditorContainer.domNode;
			this._SlicerEditorContainer = SlicerEditorContainer;
		},

		/**
		 * Toggle SlicerEditor visibility
		 */
		show: function (visible) {
			if (!this._created) {
				this._create();
			}

			html.setVisible(this._domNode, visible);
		},

		/**
		 * Update configuration with new set of selected classifiers, update slicer.
		 * @param {string} popupType
		 * @param {string[]} newClassifiers - new set of selected classifiers
		 */
		_updateConfig: function (popupType, newClassifiers) {
			// Save references to containers
			var colContainer = this._config.columnClassifiers.container;
			var rowContainer = this._config.rowClassifiers.container;
			var metricContainer = this._config.metrics.container;

			if (popupType == B_ROW_CLS) {
				this._config.columnClassifiers = _.difference(this._config.columnClassifiers, newClassifiers);
				this._config.rowClassifiers = newClassifiers;
			} else if (popupType == B_COL_CLS) {
				this._config.rowClassifiers = _.difference(this._config.rowClassifiers, newClassifiers);
				this._config.columnClassifiers = newClassifiers;
			} else if (popupType == B_METRICS) {
				this._config.metrics = newClassifiers;
			}
			// Restore References to containers
			this._config.columnClassifiers.container = colContainer;
			this._config.rowClassifiers.container = rowContainer;
			this._config.metrics.container = metricContainer;

			this._slicerConfig.set(this._config);
		},

		/**
		 * Sumit results of row classifiers options menu
		 * @param {Object} newRowClsOptions - new set of options
		 */
		_updateRowClsOptions: function (newRowClsOptions) {
			this._config.expandAll = newRowClsOptions.indexOf('expandAll') >= 0;
			this._config.stickyRows = newRowClsOptions.indexOf('stickyRows') >= 0;

			const oldFlatSlicer = this._config.flatSlicer;
			this._config.flatSlicer = newRowClsOptions.indexOf('flatSlicer') >= 0;

			const bQuickUpdate = oldFlatSlicer == this._config.flatSlicer;
			this._slicerConfig.set(this._config, bQuickUpdate);
		},


		/**
		 * Recreate slicer according to current SlicerEditor
		 */
		_recreateSlicer: function () {
			this._slicerConfig.recreatePivotConfig(this._config.rowClassifiers.container, this._config.columnClassifiers.container, this._config.metrics.container, '.dijitTitlePaneTitle');
		},

		/**
		 * Mark specified metric name and container scroll position
		 * @param {string}
		 */
		markPane: function (cls) {
			if (cls) {
				activePaneCls = cls;
			}
			scrollPos = this._SlicerEditorContainer.domNode.scrollTop;
		},

		/**
		 * Restore marked metric expand position and scroll to it
		 */
		restoreMetricPane: function () {
			var self = this;
			// restore scroller position after redrawing;
			if (scrollPos) {
				_.delay(function () {
					self._SlicerEditorContainer.domNode.scrollTop = scrollPos;
					scrollPos = 0;
				}, 0);
			}
		},

		/**
		 * Create add/edit/remove highlight actions and InlineActionMenu for metric panel
		 * @param {string} title
		 * @param {string} cls
		 * @return {InlineActionMenu}
		 */
		createMetricMenu: function (title, cls) {
			var self = this;
			var highlightActions = [];
			if (!this._config.metricsHl[cls]) {
				highlightActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerAddColorHighlight",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerAddHlRule"),
						m_onAction: function (ac) { self._onActionAddColorHighlight(ac, self, cls, title); }
					}));
			} else {
				highlightActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerEditColorHighlight",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerEditHlRule"),
						m_onAction: function (ac) { self._onActionEditColorHighlight(ac, self, cls, title); }
					}));
				highlightActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerRemoveColorHighlight",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerRemoveHlRule"),
						m_onAction: function (ac) { self._onActionRemoveColorHighlight(ac, self, cls); }
					}));
			}
			var store = this._config.metricStore;
			var metric = store.get(cls);
			if (metric.custom) {
				var widget = this._slicerWidget;
				highlightActions.push(
					lang.mixin(new Action(), {
						separator: true
					}));
				highlightActions.push(
					lang.mixin(new Action(), {
						m_id: "SlicerEditCustomMetric",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerEditCustomMetric"),
						m_onAction: function (ac) { self._onActionAddEditCustomMetric(metric); }
					}));
				highlightActions.push(
					lang.mixin(new DeleteMetricAction(), {
						metric: metric,
						viewKey: widget.getViewPreferenceKey(),
						customViewId: widget.customViewId,
						m_onAction: function (ac) { self._onActionDeleteCustomMetric(metric); }
					}));
			}

			return new InlineActionMenu({ // Create metrics "..." menu button and action menu
				label: "",
				'class': 'csi-row-actions-menu csi-slicer-metrics-menu',
				showLabel: false,
				bOpenOnHover: false,
				bToggleOpen: true,
				actions: highlightActions,
				materialIcon: "more_horiz",
				onAction: this._onActionsMenuClick
			});
		},

		/**
		 * Create content of metric pane's dropdown;
		 * @param {string} cls - metric key;
		 * @param {string} clsName - metric name;
		 * @return {node} - return created node;
		 */
		createMetricDropdown: function (cls, clsName) {
			var self = this;
			var div = html.create('div', {});
			if (!this._config.metricsHl[cls]) { // if highlight rules are not yet created - create link button
				var title = locale.resource("SlicerAddHlRule");
				new Button({ // Create close button
					'class': 'csi-link-btn',
					label: title,
					onClick: function () {
						self.markPane(cls);
						self._onActionAddColorHighlight(null, self, cls, title + ' - ' + clsName);
					}
				}).placeAt(div);
			} else { // existent hl rules
				new ToggleButton({ // turn highlighting on/off switch
					label: locale.resource("SlicerShowHl"),
					showLabel: true,
					'class': "csi-button-switch",
					checked: this._config.metricsHlVisible[cls],
					disabled: false,
					onChange: function (val) {
						_.delay(function () { // allow toggle button animation;
							self.markPane(cls);
							self._config.metricsHlVisible[cls] = val;
							self._slicerConfig.set(self._config);
						}, 300);
					}
				}).placeAt(div);

				// display hl rules
				var spec = this._getSpec(cls);
				_.each(this._config.metricsHl[cls], function (rule) {
					var ruleDiv = html.create('div', {}, div);
					var ruleText = '';

					switch (rule.condition) {
						case constants.SLICER_HL_LESS: ruleText = CHAR_LT;
							break;
						case constants.SLICER_HL_LESS_EQ: ruleText = CHAR_LTE;
							break;
						case constants.SLICER_HL_GREATER: ruleText = CHAR_GT;
							break;
						case constants.SLICER_HL_GREATER_EQ: ruleText = CHAR_GTE;
							break;
						case constants.SLICER_HL_BETWEEN: ruleText = 'and&nbsp;&nbsp;' + CHAR_LTE;
							break;
						case constants.SLICER_HL_OTHER: ruleText = 'other';
					}

					html.create('span', { 'class': 'csi-slicer-color-hl csi-slicer-color-hl-' + rule.color }, ruleDiv);
					var bPercent = spec.format && (spec.format.indexOf("%") >= 0);
					var val = spec.ren.formatValue(bPercent ? Number(rule.value) / 100 : Number(rule.value), spec.format);
					if (rule.condition == constants.SLICER_HL_BETWEEN) {
						html.create('span', { 'class': 'csi-clicer-metric-info', innerHTML: CHAR_GTE + ' ' + val }, ruleDiv);
						val = spec.ren.formatValue(bPercent ? Number(rule.value2) / 100 : Number(rule.value2), spec.format);
					}
					html.create('span', { 'class': 'csi-clicer-metric-info', innerHTML: ruleText }, ruleDiv);
					if (rule.condition != constants.SLICER_HL_OTHER) {
						html.create('span', { 'class': 'csi-clicer-metric-info', innerText: val }, ruleDiv);
					}
				});
			}
			return div;
		},

		/**
		 * Create content of row classifier pane's dropdown;
		 * @param {string} cls - classifier name;
		 * @return {node} - return created node;
		 */
		createColClsDropdown: function (cls) {
			var self = this;
			var div = html.create('div', { 'class': 'center' });
			new ToggleButton({ // turn highlighting on/off switch
				label: locale.resource("SlicerShowTotals"),
				showLabel: true,
				'class': "csi-button-switch",
				checked: this._config.showTotals[cls],
				disabled: false,
				onChange: function (val) {
					_.delay(function () { // allow toggle button animation;
						self.markPane(cls);
						self._config.showTotals[cls] = val;
						self._slicerConfig.set(self._config);
					}, 300);
				}
			}).placeAt(div);
			return div;
		},

		/**
		 * Create actions and InlineActionMenu for row classifiers options
		 * @param {string} title - classifiers panel title
		 * @return {InlineActionMenu}
		 */
		createColClsOptions: function (title) {
			var self = this;

			var colClsActions = [];
			var cmp = this._config.compareTo;
			if (!cmp || !cmp.key) {
				colClsActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerAddComparison",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerAddComparison"),
						m_onAction: function (ac) { self._onActionAddComparison(ac, self); }
					}));
			} else {
				colClsActions.push(
					lang.mixin(this._actionCompEdit = new Action(), {
						m_id: "slicerEditComparison",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerEditComparison"),
						m_onAction: function (ac) { self._onActionEditComparison(ac, self); }
					}));
				colClsActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerRemoveComparison",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerRemoveComparison"),
						m_onAction: function (ac) { self._onActionRemoveComparison(ac, self); }
					}));
			}
			colClsActions.push(
				lang.mixin(new Action(), {
					m_id: "slicerResetColWidth",
					m_typeControl: def.LINK,
					m_name: locale.resource("SlicerResetColWidth"),
					m_onAction: function (ac) { self._onActionResetColWidth(ac, self); }
				}));

			return new InlineActionMenu({ // Create metrics "..." menu button and action menu
				label: title + ' ' + locale.resource("Options"),
				'class': 'csi-row-actions-menu csi-slicer-icon-btn csi-slicer-rowcls-menu-btn',
				showLabel: false,
				bOpenOnHover: false,
				bToggleOpen: true,
				actions: colClsActions,
				materialIcon: "settings",
				onAction: this._onActionsMenuClick
			});
		},

		/**
		 * Create SelectList for row classifiers options
		 * @return {SelectList}
		 */
		createRowClsOptions: function () {
			var self = this;
			const config = this._config;

			var data = {
				items: [
					{ id: 'flatSlicer', name: locale.resource("SlicerFlatSlicer") }
				]
			};
			if (!this._config.flatSlicer) {
				data.items.splice(0, 0, { id: 'expandAll', name: locale.resource("SlicerExpandAllDefault") }),
				data.items.splice(1, 0, { id: 'stickyRows', name: locale.resource("SlicerStickyRows") })
			}
			var store = new Memory({ data: data });

			var selectedItems = [];
			if (config.expandAll) selectedItems.push('expandAll');
			if (config.stickyRows) selectedItems.push('stickyRows');
			if (config.flatSlicer) selectedItems.push('flatSlicer');

			return new SelectList({
				onItemChange: function () {
					const newValues = this.get('value');
					// preserve expandAll and stickyRows selection while it is hidden with flatSlicer
					if (config.flatSlicer) {
						if(config.expandAll) {
							newValues.push('expandAll');
						}
						if (config.stickyRows) {
							newValues.push('stickyRows');
						}
					}
					self._updateRowClsOptions(newValues);
					popup.close(this);
				},
				showCheckmarks: true,
				selectedFirst: false,
				showFilter: false,
				store: store,
				value: selectedItems
			});
		},

		/**
		 * Create Button for row classifiers options
		 * @param {string} title - classifiers panel title
		 * @return {Button}
		 */
		createRowClsOptionsBtn: function (title) {
			return new DropDownButton({
				'class': 'csi-slicer-icon-btn csi-slicer-rowcls-menu-btn',
				iconClass: 'material-icons',
				showLabel: false,
				materialIcon: 'settings',
				label: title + ' ' + locale.resource("Options"),
				dropDown: this.createRowClsOptions()
			});
		},

		/**
		 * Create actions and InlineActionMenu for row classifiers options
		 * @param {string} title - classifiers panel title
		 * @return {InlineActionMenu}
		 */
		createMetricsOptions: function (title) {
			var self = this;

			var customMetrics = self._slicerWidget.metricStore.query({ custom: true });
			var haveCustomMetrics = customMetrics.length > 0;

			var metricsActions = [];
			metricsActions.push(
				lang.mixin(new Action(), {
					m_id: "slicerAddCustomMetric",
					m_typeControl: def.LINK,
					m_name: locale.resource("SlicerAddCustomMetric"),
					m_onAction: function (ac) { self._onActionAddEditCustomMetric(null); }
				}));

			if (haveCustomMetrics) {
				metricsActions.push(
					lang.mixin(new Action(), {
						m_id: "slicerManageCustomMetrics",
						m_typeControl: def.LINK,
						m_name: locale.resource("SlicerManageCustomMetrics"),
						m_onAction: function (ac) { self._onActionManageCustomMetrics(ac); }
					}));
			}

			return new InlineActionMenu({ // Create metrics "..." menu button and action menu
				label: title + ' ' + locale.resource("Options"),
				'class': 'csi-row-actions-menu csi-slicer-icon-btn csi-slicer-rowcls-menu-btn',
				showLabel: false,
				bOpenOnHover: false,
				bToggleOpen: true,
				actions: metricsActions,
				materialIcon: "settings",
				onAction: this._onActionsMenuClick
			});
		},

		/**
		 * Create columns compare pane for column classifiers
		 * @return {TitlePane}
		 */
		createComparePane: function () {
			function txtNotFound(key) {
				return key + ' (' + locale.resource("NotFound") + ')';
			}
			var self = this;
			var txt = '';
			var clsValName = '';
			var slicer = this._slicerWidget._slicer;
			var cfg = this._config.compareTo;
			var div = html.create('div', { 'class': 'csi-slicer-row' });
			var clsKey = cfg.key;
			var clsObj = slicer.colClsNamesStore.query({ id: clsKey });
			var clsName = clsObj.length ? clsObj[0].name : txtNotFound(clsKey);
			var clsValKey = cfg.val;
			if (clsValKey) {
				clsObj = slicer.firstColClsValuesStore.query({ id: clsValKey });
				clsValName = clsObj.length ? clsObj[0].name : txtNotFound(clsValKey);
				txt = locale.resource('SlicerCompareToValue');
			} else {
				txt = locale.resource('SlicerCompareOn');
			}
			var span = html.create('span', { 'class': 'csi-slicer-cell', innerText: txt + ' ' }, div);
			span = html.create('span', { 'class': 'bold' }, span);
			if (clsValKey) {
				var spec = this._getSpec(clsKey);
				if (spec) {
					var id = spec.ra.getId();
					var a;

					if (spec.type == xml.ATTR_REF) {
						a = def.createBrowseAnchorForType(spec.rc.getChildClass(id), clsValKey, span);
					}

					if (a) {
						clsName = clsName || locale.getEmptyName();
					} else {
						clsValName = clsValName || locale.getEmptyName();
					}

					html.text(a || span, clsName + ':' + clsValName);
				}
			} else {
				html.text(span, clsName);
			}
			new ToggleButton({ // turn highlighting on/off switch
				label: '',
				showLabel: true,
				'class': "csi-slicer-cell csi-button-switch",
				checked: cfg.show,
				disabled: false,
				onChange: function (val) {
					_.delay(function () { // allow toggle button animation;
						self.markPane(MARK_COMPARE);
						cfg.show = val;
						self._slicerConfig.set(self._config);
					}, 300);
				}
			}).placeAt(div);

			var bOpen = false;
			if (activePaneCls == MARK_COMPARE) {
				bOpen = true;
				activePaneCls = '';
			}
			var compPane = new TitlePane({
				content: div,
				title: locale.resource("SlicerCompare"),
				'class': 'csi-slicer-classifier-pane csi-slicer-compare',
				open: bOpen,
				dragRestriction: true,
				toggleable: true
			});
			// Button "Edit Compare"
			new Button({
				label: locale.resource('SlicerEditComparison'),
				'class': 'csi-slicer-compare-edit',
				showLabel: false,
				materialIcon: "mode_edit",
				onClick: function () {
					self.markPane(MARK_COMPARE);
					csiAction.invoke(null, null, self._actionCompEdit, { bClick: true });
				}
			}).placeAt(compPane.domNode);
			// Button "Remove Classifier" - cross on the right of Title Pane
			new Button({
				label: locale.resource('SlicerRemoveComparison'),
				'class': 'csi-slicer-compare-cross',
				showLabel: false,
				materialIcon: "close",
				onClick: function () {
					self._config.compareTo = {};
					self._slicerConfig.set(self._config);
				}
			}).placeAt(compPane.domNode);
			return compPane;
		},

		/**
		 * Create content pane for one classifier or metric
		 * @param {array} classifiers
		 * @param {string} title
		 * @param {string} paneClass
		 * @param {string} iconClass
		 * @param {string[]} types - dnd types, different for row, column classifiers and metrics
		 */
		_createClassifiersPane: function (classifiers, title, paneClass, iconClass, types) {
			var self = this;
			function createMetricsPane() {
				var tp = new TitlePane({
					title: locale.resource("SlicerMetrics"),
					tooltip: locale.resource("SlicerDragDrop"),
					'class': 'csi-slicer-classifier-pane csi-slicer-metrics',
					open: false,
					toggleable: false
				});
				html.create('div', { 'class': 'csi-slicer-metrics-num', 'innerText': self._config.metrics.length }, tp.domNode);
				return tp;
			}
			// Create container title bar
			var paneTitle = html.create('div', { 'class': 'csi-slicer-classifier-title-bar' });
			html.create('span', { 'class': 'csiActionIcon ' + iconClass }, paneTitle);
			html.create('span', { 'class': 'csi-slicer-classifier-title', innerText: title }, paneTitle);

			// Determine panel type; Create classifier options: gear icon button and sub menu
			var paneType = B_UNKNOWN;
			if (types == this.dndTypesMetrics) {
				paneType = B_METRICS;
				if (crews.isInGlobalRoles(["Configuration Editor", "Slicer Editor"])) {
					this.createMetricsOptions(title).placeAt(paneTitle);
				}
			} else {
				if (types.length == 1) { // row classifiers
					paneType = B_ROW_CLS;
					this.createRowClsOptionsBtn(title).placeAt(paneTitle);
				} else { // column classifiers
					this.createColClsOptions(title).placeAt(paneTitle);
					paneType = B_COL_CLS;
				}
			}
			var isMetric = paneType == B_METRICS;
			var isColCls = paneType == B_COL_CLS;

			// Title bar's [+] button with select list popup to add/remove classifiers
			var store = (classifiers == self._config.metrics) ? self._config.metricStore : self._config.classifierStore;
			new DropDownButton({
				dropDown: new SelectList({
					'class': 'csi-slicer-selectlist',
					preserveValueOrder: true,
					store: store,
					value: classifiers,
					onClose: function () {
						self._updateConfig(paneType, this.get('value'));
					},
					// Dummy onExecute() prevents popup from being closed when user click check boxes. This function will never be called, we just need to have it.
					// File dojo/dijit/_HasDropDown defines onExecute method in function openDropDown(). It is just calling "closeDropDown()" which we want
					// to avoid, as we don't want to close this popup on checkbox click.
					// See file dojo/dijit/popup.js function "open()", line:
					// 	handlers.push(widget.on(widget.onExecute ? "execute" : "change", lang.hitch(this, function(){
					// You can see, that having onExecute function will change popup behavior.
					onExecute: function () {
					}
				}),
				label: isMetric ? locale.resource("SlicerAvailableMetrics") : locale.resource("SlicerAvailableClassifiers"),
				'class': 'csi-slicer-icon-btn',
				showLabel: false,
				materialIcon: "add_box"
			}).placeAt(paneTitle);

			// Create main pane and add title
			var thePane = new ContentPane({
				content: paneTitle,
				'class': 'csi-slicer-rows-pane',
				doLayout: false
			});

			// Drag and drop container for individual classifiers/metrics
			var classifiersContainer = classifiers.container = new GridContainerLite({
				acceptTypes: types,
				'class': 'csi-slicer-dnd-container',
				nbZones: 1,
				isAutoOrganized: false,
				scrollNode: this._SlicerEditorContainer.domNode
			});

			// Create individual classifiers/metrics panels
			_.each(classifiers, function (cls, idx) {
				if ((types == self.dndTypes) && (self._config.metricsPos == idx)) {
					classifiersContainer.addChild(createMetricsPane()); // add metric pane to column classifiers container at specified position (metrics are not at the end)
				}
				var clsObj = store.get(cls); // Either classifier or metric object
				if (clsObj) {
					var title = clsObj.name || cls;
					var bOpen = false;
					if (activePaneCls == cls) { // Open the pane (will scroll to it later)
						bOpen = true;
						activePaneCls = '';
					}
					var tp = new TitlePane({
						content: isMetric ? self.createMetricDropdown(cls, title) :
							isColCls ? self.createColClsDropdown(cls) : null,
						dndType: isMetric ? this.dndTypesMetrics[0] : this.dndTypes[DND_TYPE_CLASSIFIER],
						title: title,
						tooltip: isMetric ? (clsObj.dynamic ? clsObj.func : "") : locale.resource("SlicerDragDrop"),
						'class': 'csi-slicer-classifier-pane ' + paneClass,
						open: bOpen,
						tabIndex: bOpen ? 1 : 0,
						toggleable: isMetric || isColCls
					});
					tp.domNode.setAttribute('dndType', this.dndTypes[DND_TYPE_CLASSIFIER]);

					var titleNode = query('.dijitTitlePaneTitle', tp.domNode);
					html.setDataAttribute(titleNode[0], html.SLICER_CLASSIFIER_ID, cls);

					// create add/edit/remove highlight actions for InlineActionMenu
					if (isMetric) {
						this.createMetricMenu(title, cls).placeAt(tp.domNode);
					}

					// Button "Remove Classifier" - cross on the right of Title Pane
					new Button({
						label: locale.resource("Remove"),
						'class': isMetric ? 'csi-slicer-metric-cross' : 'csi-slicer-classifier-cross',
						showLabel: false,
						materialIcon: "close",
						onClick: function () {
							tp.destroyRecursive();
							self._recreateSlicer();
						}
					}).placeAt(tp.domNode);

					classifiersContainer.addChild(tp);
				}
			}, this);

			// Metrics bellow classifiers? Add metrics pane to the end of column classifiers container
			if ((types == self.dndTypes) && (self._config.metricsPos >= classifiers.length)) {
				classifiersContainer.addChild(createMetricsPane());
			}
			// Add container with individual classifiers
			thePane.addChild(classifiersContainer);
			// Add "Compare" Panel
			if ((paneType == B_COL_CLS) && self._config.compareTo.key) {
				thePane.addChild(this.createComparePane());
			}

			return thePane;
		},

		/**
		 * Creates SlicerEditor
		 */
		_create: function () {
			var self = this;

			this._subscribeToConfigChanges();
			this._config = this._slicerConfig.get();
			var editorTitlePane = new ContentPane({
				content: html.create('span', { 'class': 'csi-slicer-title', innerText: locale.resource("SlicerEditor") }),
				'class': 'csi-slicer-title-bar',
				doLayout: false
			});
			new Button({
				label: "",
				'class': 'csi-slicer-editor-cross',
				showLabel: false,
				materialIcon: "close",
				onClick: function () {
					self._slicerWidget.onActionShowEditor();
				}
			}).placeAt(editorTitlePane.domNode);

			this._SlicerEditorContainer.addChild(editorTitlePane);

			var classifiersPane = new ContentPane({
				'class': 'csi-slicer-classifiers',
				content: ''
			});
			this._SlicerEditorContainer.addChild(classifiersPane);

			classifiersPane.addChild(this._createClassifiersPane(this._config.rowClassifiers, locale.resource("SlicerRowClassifiers"), 'csi-slicer-classifier', 'csi-toolbar-btn-icon-slicerRows', [this.dndTypes[DND_TYPE_CLASSIFIER]]));
			classifiersPane.addChild(this._createClassifiersPane(this._config.columnClassifiers, locale.resource("SlicerColumnClassifiers"), 'csi-slicer-classifier', 'csi-toolbar-btn-icon-slicerColumns', this.dndTypes));
			classifiersPane.addChild(this._createClassifiersPane(this._config.metrics, locale.resource("SlicerMetrics"), 'csi-slicer-metrics', 'csi-toolbar-btn-icon-slicerMetrics', this.dndTypesMetrics));

			this.own(topic.subscribe("/dojox/mdnd/drop", function (node, sourceArea, dropIndex) {
				if (domClass.contains(node, 'csi-slicer-classifier-pane')) { // detect slicerEditor event (ignore grid's drag drop)
					self._recreateSlicer();
				}
			}));

			this._created = true;
		},

		/**
		 * On destroy
		 */
		destroy: function () {
			this._unsubscribeFromConfigChanges();

			this.inherited(arguments);
		},

		/**
		 * Action invocation
		 * @param {Action} a
		 */
		_onActionsMenuClick: function (a) {
			try {
				csiAction.invoke(null, null, a, { bClick: true });
			} catch (ex) {
				console.warn(ex);
			}
		},

		/**
		 * @param {Object} dlg
		 * @param {str} cls
		 */
		_saveHlRules: function (dlg, cls) {
			var cfg = this._config;
			if (dlg.hlRules.length) {
				cfg.metricsHl[cls] = dlg.hlRules;
			} else {
				delete cfg.metricsHl[cls];
			}
			dlg.hide();
			this._slicerConfig.set(cfg);
		},

		/**
		 * Add new set of color highlighting rules
		 * @param {Object} ac
		 * @param {Object} self
		 * @param {string} cls
		 * @param {string} title
		 */
		_onActionAddColorHighlight: function (ac, self, cls, title) {
			var spec = this._getSpec(cls);
			var dlg = new SlicerColorDlg({
				title: ac ? ac.getAction().getName() + ' - ' + title : title,
				hlRules: {},
				metricSpec: spec,
				onSave: function () {
					self._config.metricsHlVisible[cls] = true;
					self._saveHlRules(this, cls); // this == dlg instance
				}
			});
			dlg.startup();
			dlg.show();
			self.markPane(cls);
		},

		/**
		 * Edit existing set of color highlighting rules
		 * @param {Object} ac
		 * @param {Object} self
		 * @param {string} cls
		 * @param {string} title
		 */
		_onActionEditColorHighlight: function (ac, self, cls, title) {
			var spec = this._getSpec(cls);
			var cfg = self._config;
			var dlg = new SlicerColorDlg({
				title: ac.getAction().getName() + ' - ' + title,
				hlRules: _.clone(cfg.metricsHl[cls]),
				metricSpec: spec,
				onSave: function () {
					self._saveHlRules(this, cls); // this == dlg instance
				}
			});
			dlg.startup();
			dlg.show();
			self.markPane(cls);
		},

		/**
		 * Remove set of color highlighting rules, no dialog shown.
		 * @param {Object} ac
		 * @param {Object} self
		 * @param {string} cls
		 */
		_onActionRemoveColorHighlight: function (ac, self, cls) {
			var cfg = self._config;
			delete cfg.metricsHl[cls];
			self.markPane(cls);
			self._slicerConfig.set(cfg);
		},

		/**
		 * Save Column Compare dialog results changes.
		 * @param {slicerCompareDlg} dlg
		 */
		_saveComparison: function (dlg) {
			var cfg = this._config;
			cfg.compareTo = dlg.cfg;
			dlg.hide();
			this._slicerConfig.set(cfg);
		},

		/**
		 * Create Custom Metric and add it to selection
		 * @param {Object} metric - metric object when editing action, or null for adding new
		 * @param {Function} onResponse
		 * @param {Function} onErr
		 */
		_onActionDeleteCustomMetric: function (metric, onResponse, onErr) {
			var self = this;
			var widget = this._slicerWidget;
			var params = {
				StoreName: widget.defView.slicerStore,
				MetricName: metric.id
			};
			// Slicer Server API documentation: https://centric8.atlassian.net/wiki/spaces/DEV/pages/171573303/Slicer+Server+API
			var request = new PostRequest("SiteAdmin", "DeleteCustomDynamicMetric", params);
			request.go().then(
				function (response) {
					onResponse ? onResponse(response, metric) : self.refreshWidget(); // slicerWidget._resolvePreferences() will filter out deleted metric ( this.metricKeys = _.filter... )
				},
				function (err) {
					onErr ? onErr(err) : new MessageDialog({
						destroyOnHide: true,
						buttons: [MessageDialog.BUTTON_OK],
						message: err.message,
						materialIcon: MessageDialog.ICON_ERROR,
						title: 'Error'
					}).show();
				});
		},

		/**
		 * Create Custom Metric and add it to selection
		 * @param {Object} metric - metric object when editing action, or null for adding new
		 * @param {Manager} manageGrid - this dialog called from Manage Metrics dialog
		 */
		_onActionAddEditCustomMetric: function (metric, manageGrid) {
			var self = this;
			var fullStore = this._config.metricStore;
			// Filter out custom metrics and metrics with type Date - they can't be used in Custom Metris dialog
			var storeData = fullStore.query({ custom: false }).filter((item) => !types.isTimeFormat(item.heading.m_format));
			var store = new Memory({ data: storeData });
			store.put({ id: "Placeholder", name: "" });

			var dlg = new ViewDialog({
				bSubmitOnEnter: true,
				dialogActions: true,
				destroyOnHide: true,
				content: new slicerMetricsDlg({ metricStore: store, fullMetricStore: fullStore, value: metric }),
				title: metric ? locale.resource("SlicerEditCustomMetric") : locale.resource("SlicerAddCustomMetric"),
				'class': 'csi-slicer-metrics-dlg',
				onSave: function () {
					self.refreshRequired = true;
					var dlgSelf = this;
					var newMetric = this.content.get("value");
					var params = newMetric.params;
					params.StoreName = self._slicerWidget.defView.slicerStore;
					var op = metric ? "ChangeCustomDynamicMetric" : "CreateCustomDynamicMetric";
					// Slicer Server API documentation: https://centric8.atlassian.net/wiki/spaces/DEV/pages/171573303/Slicer+Server+API
					var request = new PostRequest("SiteAdmin", op, params);
					request.go().then(
						function (response) {
							dlgSelf.hide();
							if (manageGrid) { // Manage Metrics dialog
								// update Manage dialog without refreshing entire view
								if (metric) { // remove existing metric
									fullStore.remove(metric.id);
									manageGrid.store.remove(metric.id);
								}
								fullStore.put(newMetric); // Update fullStore to allow function slicerMetricsDlg._createKey() work properly
								// create temporary new metric in manage grid
								manageGrid.store.put(newMetric);
								manageGrid.refresh();
							} else {
								if (metric) { // Edit Metric (called from metric)
									self.markPane();
									self.refreshWidget();
								} else { // Add new metric to the view (called from metrics pane popup menu)
									var metrics = self._config.metrics;
									var idx = metrics.indexOf(newMetric.id);
									if (idx < 0) { // if metric is not already there
										self._config.metrics.push(newMetric.id);
										self._slicerConfig.set(self._config);
									}
								}
							}
						},
						function (err) {
							dlgSelf.content.invalidNode.set('value', err.message);
						}
					);
				},
				/***
				 * Overwrite default function to make "onSave" always works. We use our own validation methods.
				 * @return {boolean} - always return true
				 */
				_hasValueChanged: function () {
					return true;
				}
			});
			dlg.startup();
			dlg.show();
		},

		/**
		 * Manage custom metrics (list, edit, delete)
		 * @param {Object} ac
		 * @param {Object} self
		 */
		_onActionManageCustomMetrics: function (ac) {
			var self = this;
			var columns = [
				{ field: "name", label: locale.resource("Name") },
				{ field: "func", label: locale.resource("Function") }
			];
			var pane = new ContentPane();
			var errMsg = new InvalidMessage().placeAt(pane);
			var customMetricsStore = new Memory({ data: this._config.metricStore.query({ custom: true }) });
			var grid = new declare([Manager, Selection])({
				allowTextSelection: true,
				bConfirmDelete: true,
				confirmDeletePromptEntity: locale.resource("SlicerCustomMetric"),
				confirmDeleteMessage: locale.resource("SlicerDeleteCustomMetric_Confirm"),
				columns: columns,
				customActions: [{ name: locale.resource("Edit"), icon: "edit", callback: "_onEditMetric" },
				{ name: locale.resource("Delete"), icon: "close", callback: "_onDeleteMetric" }],
				selectionMode: 'single',
				sort: [{ attribute: 'name' }],
				store: customMetricsStore,
				_onEditMetric: function (item) {
					self._onActionAddEditCustomMetric(item.data, grid);
				},
				_onDeleteMetric: function (item) {
					var widget = self._slicerWidget;
					var a = lang.mixin(new DeleteMetricAction(), {
						metric: item.data,
						viewKey: widget.getViewPreferenceKey(),
						customViewId: widget.customViewId,
						m_onAction: function (ac) { self._onActionDeleteCustomMetric(item.data, grid.onDeleteResponse, grid.onDeleteErr); }
					});
					csiAction.invoke(null, null, a, { bClick: true });
				},
				onDeleteResponse: function (response, metric) {
					self.refreshRequired = true;
					self._config.metricStore.remove(metric.id);
					customMetricsStore.remove(metric.id);
					grid.refresh();
				},
				onDeleteErr: function (err) {
					errMsg.set('value', err.message);
				}
			});
			pane.addChild(grid);

			var dlg = new ViewDialog({
				bSubmitOnEnter: true,
				dialogActions: true,
				destroyOnHide: true,
				content: pane,
				submitResource: "Close",
				showCancelButton: false,
				title: ac.getAction().getName(),
				'class': 'csi-slicer-manage-dlg',
				onSave: function () {
					this.hide();
				},
				onHide: function () {
					if (self.refreshRequired) {
						self.refreshWidget();
					}
				},
				_hasValueChanged: function () {
					return true;
				},
				onRenderDialogActions: function (node) {
					new Button({
						"class": html.CSS_ACTION + " " + html.CSS_BUTTON_SECONDARY,
						label: locale.resource("SlicerAddCustomMetric"),
						onClick: function () {
							self._onActionAddEditCustomMetric(null, grid);
						}
					}).placeAt(node, 0);
				}
			});
			dlg.startup();
			dlg.show();
		},

		/**
		 * Add column comparison, show comparison configuration popup dialog
		 * @param {Object} ac
		 * @param {Object} self
		 */
		_onActionAddComparison: function (ac, self) {
			var slicer = this._slicerWidget._slicer;
			var cfg = this._config;
			var dlg = new slicerCompareDlg({
				title: ac.getAction().m_name,
				cfg: {},
				firstColClsValuesStore: slicer.firstColClsValuesStore,
				colClsNamesStore: slicer.colClsNamesStore,
				metricsPos: cfg.metricsPos,
				onSave: function () {
					// this == dlg instance
					this.cfg.show = true;
					self.columnCompareKey = this.cfg.key; // because we importing from old slicer in slicerWidget._buildView()
					self._saveComparison(this);
				}
			});
			dlg.startup();
			dlg.show();
		},

		/**
		 * Edit column comparison: show comparison configuration popup dialog
		 * @param {Object} ac
		 * @param {Object} self
		 */
		_onActionEditComparison: function (ac, self) {
			var slicer = this._slicerWidget._slicer;
			var cfg = this._config;
			var dlg = new slicerCompareDlg({
				title: ac.getAction().m_name,
				cfg: cfg.compareTo,
				firstColClsValuesStore: slicer.firstColClsValuesStore,
				colClsNamesStore: slicer.colClsNamesStore,
				metricsPos: cfg.metricsPos,
				onSave: function () {
					// this == dlg instance
					self.columnCompareKey = this.cfg.key; // because we importing from old slicer in slicerWidget._buildView()
					self._saveComparison(this);
				}
			});
			dlg.startup();
			dlg.show();
		},

		/**
		 * Remove column comparison
		 * @param {Object} ac
		 * @param {Object} self
		 */
		_onActionRemoveComparison: function (ac, self) {
			self._config.compareTo = {};
			self._config.columnCompareKey = null;
			self._slicerConfig.set(self._config);
		},

		/**
		 * Reset slicer column widths
		 * @param {Object} ac
		 * @param {Object} self
		 */
		_onActionResetColWidth: function (ac, self) {
			self._config.columnSizes = {};
			self._slicerConfig.set(self._config);
		},

		/**
		 * Callback function, called from SlicerConfig on new config set
		 * @param {Object} widget - instance of slicerWidget
		 */
		_updateProps: function (widget) {
			var self = widget._slicerEditor;
			self.markPane();
			var rowContainer = self._config.rowClassifiers.container;
			var colContainer = self._config.columnClassifiers.container;
			var metricsContainer = self._config.metrics.container;
			self._config = widget._slicerConfig.get();
			self._config.rowClassifiers.container = rowContainer;
			self._config.columnClassifiers.container = colContainer;
			self._config.metrics.container = metricsContainer;
		},

		/**
		 * Refresh widget. Fully reload and recreate slicer view.
		 */
		refreshWidget: function () {
			this._slicerWidget.metricStore = null; // force slicerWidget.loadViewClasses() to refresh stores
			this._slicerWidget.refreshWidget();
		},

		/**
		 * Set function this._updateProps() as a call back to any configuration changes
		 */
		_subscribeToConfigChanges: function () {
			this._cfgHandle = this._slicerConfig.subscribe(this._updateProps);
		},

		/**
		 * Remove function this._updateProps() as a call back to any configuration changes
		 */
		_unsubscribeFromConfigChanges: function () {
			if (this._cfgHandle) {
				this._slicerConfig.unsubscribe(this._cfgHandle);
				this._cfgHandle = null;
			}
		}

	});
});
